import { useQuery } from 'react-query';
import { PAYOUT_STATUS_SCREEN } from '../appConstants/APIEndpoint';
import { requestApiPost } from './index';
import type {
  IPayoutStatusRequest,
  IProcessWithdrawalData,
  IStatusScreen,
} from '../interfaces/Withdrawal';

/**
 * Fetches the payout status screen — step 4 of the flow.
 * Mirrors `WithdrawStatusViewModel.getPayoutStatusScreenData`.
 *
 * Android does NOT poll this. The server pushes `REFRESH_WITHDRAW_STATUS_SCREEN`
 * over the socket with a `sessionId`, and the client refetches after a 500ms
 * debounce. The PWA does the same, with a manual refresh as a fallback.
 */
export const fetchPayoutStatus = async (
  body: IPayoutStatusRequest,
): Promise<IStatusScreen | undefined> => {
  const res = await requestApiPost<IProcessWithdrawalData>(
    PAYOUT_STATUS_SCREEN,
    body,
  );
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'Could not fetch the withdrawal status');
  }
  return response?.data?.statusScreen;
};

/**
 * Just long enough to bridge `processWithdrawal` → mount (~17ms in practice), so
 * the screen does not immediately re-ask for the screen it was handed.
 *
 * `initialDataUpdatedAt` cannot do this on its own: react-query defaults
 * `staleTime` to 0, so seeded data is stale the moment it lands and mount
 * refetches regardless. That refetch used to replace `progress` with `pending`
 * before its countdown or CTA delay could be seen.
 *
 * Deliberately short. This same knob also gates `refetchOnReconnect`, and with
 * neither polling nor a manual refresh on this screen, that resync is one of the
 * few ways to recover a missed socket push. Socket invalidation bypasses
 * `staleTime` entirely, so pushes always refetch at once.
 */
const STATUS_STALE_TIME_MS = 2000;

interface IUsePayoutStatus {
  sessionId?: string;
  localeSelected?: string | null;
  /**
   * The screen `processWithdrawal` already returned. Seeded as initial data so
   * the status paints immediately without a loading flash.
   */
  initialStatusScreen?: IStatusScreen;
  enabled?: boolean;
}

export const usePayoutStatus = ({
  sessionId,
  localeSelected = null,
  initialStatusScreen,
  enabled = true,
}: IUsePayoutStatus) => {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['payoutStatus', sessionId, localeSelected],
    queryFn: () => fetchPayoutStatus({ sessionId, localeSelected }),
    // Must stay enabled even when seeded: the socket refresh works by
    // invalidating this key, and a disabled query never refetches.
    enabled: enabled && Boolean(sessionId),
    initialData: initialStatusScreen,
    // Both are needed to stop the mount refetch — see STATUS_STALE_TIME_MS.
    initialDataUpdatedAt: initialStatusScreen ? Date.now() : undefined,
    staleTime: STATUS_STALE_TIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    statusScreen: data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

export default usePayoutStatus;
